<template>
  <!-- Category Items Panel -->
  <div class="tasks-layout__content">
    <!-- main content header -->
    <div class="tasks-layout__header">
      <div
        v-if="!searchIsOpen"
        class="typo-nav-item tasks-layout__title"
        @click="$emit('showCategories')"
      >
        {{ title }}
        <UseSvg name="arrow_down_16" class="icon icon--small hide-for-medium" />
      </div>
      <div v-else class="input input--flex-grow input--tasks">
        <div :class="{ 'input__prefix': true, 'input__prefix--hilight': search }">
          <UseSvg name="search" class="icon" />
        </div>
        <input
          :aria-label="$t('tasks.search')"
          :placeholder="searchPlaceholderText"
          v-model="search"
          ref="searchInput"
        />
        <button v-if="search || searchIsOpen" class="input__clear" @click="clearSearch">
          <UseSvg
            :aria-label="$t('general.close_search')"
            :name="searchIsOpen ? 'close_circle_24' : 'close_circle_16'"
            :class="{ 'icon': true, 'icon--small': !searchIsOpen }"
          />
        </button>
      </div>

      <div class="tasks-layout__header-right">
        <UseSvg
          v-if="!searchIsOpen"
          name="search"
          class="icon"
          @click="openSearch"
          :aria-label="$t('tasks.search_tasks')"
        />
        <div
          v-if="breakpointMedium && category === 'issues_missing_tasks'"
          class="tasks-layout__filters typo-small"
        >
          <FilterDropdown
            :filterCount="filters['priority'].length"
            :label="$t('tasks.priority')"
            :options="taskPriorityOptions"
            @applyFilters="applyFilters"
            @clearFilters="clearFilters('priority')"
          />
          <FilterDropdown
            :filterCount="filters['condition'].length"
            :label="$t('tasks.condition')"
            :options="taskConditionOptions"
            @applyFilters="applyFilters"
            @clearFilters="clearFilters('condition')"
          />
        </div>
      </div>
    </div>

    <!-- main content sub header -->
    <div
      v-if="category === 'issues_missing_tasks'"
      class="tasks-layout__sub-header hide-for-medium"
    >
      <FilterDropdown
        :filterCount="filters['priority'].length"
        :label="$t('tasks.priority')"
        :options="taskPriorityOptions"
        @applyFilters="applyFilters"
        @clearFilters="clearFilters('priority')"
      />
      <FilterDropdown
        :filterCount="filters['condition'].length"
        :label="$t('tasks.condition')"
        :options="taskConditionOptions"
        @applyFilters="applyFilters"
        @clearFilters="clearFilters('condition')"
      />
    </div>

    <!-- main content sub header -->
    <EmptyMessage
      v-if="issues && issues.length === 0"
      v-bind="emptyState"
    >
      <CircleSquareTarget />
    </EmptyMessage>
    <div class="tasks-layout__tasks-list">
      <template v-if="search && searchResults">
        <RecyclerView
          v-if="searchResults.length > 0"
          class="issue-list issue-list--recycler-view scroll-container"
          :count="searchResults.length"
          :height="issueCardHeight"
          :gap="issueCardGap"
          name="issue-list__list-item-"
        >
          <template #default="{ index, style }">
            <IssueCard
              :key="searchResults[index].id"
              :issue="searchResults[index]"
              :style="style"
              :tags="true"
              :modifiers="['responsive']"
            />
          </template>
        </RecyclerView>
        <EmptyMessage v-else :message="$t('general.no_results')">
          <UseSvg name="magnifying_glass_circle" />
        </EmptyMessage>
      </template>
      <RecyclerView
        v-if="!loading && !searchResults"
        class="issue-list issue-list--recycler-view scroll-container"
        :count="filteredIssues.length"
        :height="issueCardHeight"
        :gap="issueCardGap"
        name="issue-list__list-item-"
      >
        <template #default="{ index, style }">
          <IssueCard
            :key="filteredIssues[index].id"
            :issue="filteredIssues[index]"
            :style="style"
            :tags="true"
            :modifiers="['responsive']"
          />
        </template>
      </RecyclerView>
      <Loader class="tasks-layout__loader" v-if="loading" />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from 'vue';
import { useRoute } from 'vue-router';
import I18n from 'i18n-js';

// Utils
import { useBreakpoint } from '@/functions/screen_width';
import useAjax from '@/functions/use_ajax';
import Searcher from '@/functions/searcher';
import useOrganisationProfile from '@/functions/use_organisation_profile';
import { PRIORITY_FILTER_VALUES } from '@/functions/priority';

// Components
import CircleSquareTarget from '@/images/circle-square-target.svg';
import EmptyMessage from '@/components/generic/empty_message.vue';
import Loader from '@/components/generic/loader.vue';
import IssueCard from '@/components/issues/issue_card.vue';
import RecyclerView from '@/components/generic/recycler_view.vue';
import UseSvg from '@/components/generic/use_svg.vue';
import FilterDropdown from '@/components/tasks/filter_dropdown.vue';

const props = defineProps({
  category: { type: String, required: true },
});
const emit = defineEmits(['showCategories', 'updateCategoryCount']);

const route = useRoute();
const ajax = useAjax();

const loading = ref(true);
const issues = ref(null);

const activeBreakpoint = useBreakpoint();
const search = ref('');
const searchInput = ref(null);
const searchResults = ref(null);
const searchIsOpen = ref(false);
const breakpointMedium = ref(null);
const filters = ref({
  condition: [],
  priority: [],
});

const { priorityLabel } = useOrganisationProfile();

let searcher;

/**
 * Filter (conditions) options
 */
const taskConditionOptions = [5, 4, 3, 2, 1].map((type) => ({
  value: type,
  label: I18n.t('tasks.condition_number', { number: type }),
}));

/**
 * Filter (rating) options
 */
const taskPriorityOptions = computed(() => PRIORITY_FILTER_VALUES.map((priority) => ({
  value: `priority_${priority}`,
  label: priorityLabel(priority),
})));

/**
 * Renders category title
 */
const title = computed(() => I18n.t(`tasks.categories.${props.category}`));

const searchPlaceholderText = computed(() => {
  return I18n.t('tasks.search_category', { category: I18n.t(`tasks.categories.${props.category}`).toLowerCase() });
});

const issueCardHeight = computed(() => {
  if (
    activeBreakpoint.value === 'small' ||
    activeBreakpoint.value === 'medium'
  ) {
    return 4.25;
  } else {
    return 10.5;
  }
});

const issueCardGap = computed(() => {
  if (
    activeBreakpoint.value === 'small' ||
    activeBreakpoint.value === 'medium'
  ) {
    return 0.125;
  } else {
    return 0.75;
  }
})

const emptyState = computed(() => {
  return {
    title: I18n.t(`empty_states.tasks.${props.category}.title`),
    message: I18n.t(`empty_states.tasks.${props.category}.message`),
  };
});

function trackWindowResize() {
  if (window.innerWidth > 768) {
    return breakpointMedium.value = true;
  } else {
    return breakpointMedium.value = false;
  }
}

/**
 * Filtered tasks
 */
const filteredIssues = computed(() => {
  const shouldFilter = ['condition', 'priority'].some((filter) => filters.value[filter]?.length > 0);
  if (!shouldFilter) return issues.value;

  let filteredIssues = [...issues.value];

  if (filters.value['condition'].length > 0) filteredIssues = filterByCondition(filteredIssues);
  if (filters.value['priority'].length > 0) filteredIssues = filterByPriority(filteredIssues);

  return filteredIssues;
});

onMounted(() => {
  loadIssues();
  trackWindowResize();
  window.addEventListener('resize', trackWindowResize);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', trackWindowResize);
});

watch(() => props.category, loadIssues);

/**
 * Watch and set search
 */
watch(search, () => {
  if (search.value) {
    searchResults.value = searcher.search(search.value);
  } else {
    searchResults.value = null;
  }
});

/**
 * Fetch & load issues
 */
async function loadIssues() {
  const { organisationId, operationId } = route.params;

  loading.value = true;
  clearSearch();

  let { ok, data } = await ajax.paginated(
    `/organisations/${organisationId}/operations/${operationId}/plants/issues`,
    { query: { category: props.category } },
  );
  if (ok) {
    issues.value = data;
    emit('updateCategoryCount');
    searcher = new Searcher(issues.value, { keys: ['title', 'description', 'id'] });
  }

  loading.value = false;
}

/**
 * Filter by condition only
 */
function filterByCondition(issues) {
  return issues.filter((issue) => {
    return filters.value['condition'].includes(issue?.overall_condition_rating);
  });
}

/**
 * Filter by condition only
 */
function filterByPriority(issues) {
  return issues.filter((issue) => {
    return filters.value['priority'].includes(`priority_${issue?.priority}`);
  });
}

/**
 * Apply filters
 */
function applyFilters(label, selectedOptions) {
  filters.value[label.toLowerCase()] = [...selectedOptions];
}

/**
 * Clear filters
 */
function clearFilters(label) {
  filters.value[label.toLowerCase()] = [];
}

async function openSearch() {
  search.value = '';
  searchIsOpen.value = true;

  await nextTick();
  searchInput.value.focus();
};

function clearSearch() {
  search.value = '';
  searchIsOpen.value = false;
}
</script>
